import React, { useEffect, useState, createRef } from "react";
import styled from "styled-components";
import PrimaryNavigation from "../PrimaryNavigation/PrimaryNavigation";
import BurgerButton from "../BurgerButton/BurgerButton";
import Icon from "../../Icon/Icon";
import { Device } from "../../../../components/device";
import { MenuItem } from "../../../../typings/typings";
import Anchor from "../../../../components/Router/Anchor";
import { useLocation } from "react-router";
import UserStatus from "../../../../containers/UserStatus/UserStatus";

interface AppBarWrapProps {
    //The app bar color. (Default: blacks)
    defaultColor?: string;
    /**
     * Wether or not to collapse the container, and overlap content.
     * Default does not overlap content.
     */
    overContent?: boolean;
}

const AppBarWrap = styled.header<AppBarWrapProps>`
    --app-bar-height: 60px;
    --app-bar-color: ${(props) => props.defaultColor || "black"};
    --app-bar-color-focus: white;
    ${Device.tabletLandscape} {
        --app-bar-height: 150px;
    }

    font-family: "Josefin Sans", "Lato", sans-serif;
    height: ${(props) => (props.overContent ? "auto" : "var(--app-bar-height, 150px)")};

    button:focus {
        outline: none;
    }

    &.app-bar--dropdown {
        background-color: transparent;
        .app-bar--active {
            background: transparent;
        }
    }

    .app-bar__btn-cart {
        display: flex;
        align-items: center;
    }

    .app-bar__btn-cart-title {
        display: none;
        ${Device.laptop} {
            display: inline-block;
        }
    }

    .app-bar__btn-login {
        &-title {
            display: none;
            ${Device.laptop} {
                display: inline-block;
            }
        }
        & > span:not(.app-bar__btn-login-title) {
            // position: relative;
            // top: 4px;
            // margin-right: 4px;
        }
    }

    .app-bar__count {
        padding: 4px 4px 1px 4px;
        line-height: 1;
        margin-left: 5px;
        display: none;
        position: relative;
        top: -4px;
        ${Device.laptop} {
            display: inline-block;
            top: 0px;
        }
    }
`;

const AppBarInnerWrap = styled.div`
    background-color: transparent;
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    height: var(--app-bar-height, 60px);
    width: 100%;
    z-index: 100;

    &.app-bar--minimal {
        background: white;
        color: black;
        position: fixed;
        visibility: hidden;
        ${Device.tabletLandscape} {
            height: 60px;
        }
    }

    .button--active {
        position: relative;
        color: var(--app-bar-color-focus, blue);

        &:before {
            content: "";
            width: 100%;
            height: 2px;
            position: absolute;
            bottom: 0px;
            left: 0;
            border: 1px solid;
            animation: slideIn 0.4s both;
        }
    }

    @keyframes slideIn {
        0% {
            width: 0;
        }
        100% {
            width: 100%;
        }
    }
`;

const AppBarContainer = styled.div`
    width: 100%;
    height: 100%;
    align-items: center;
    padding: 0 1rem;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    ${Device.tabletLandscape} {
        justify-content: center;
    }
    ${Device.laptop} {
        padding-left: 1.5rem;
        padding-right: 1.5rem;
    }
`;

const AppBarLogo = styled.a<{ textcolor?: string }>`
    display: block;
    svg {
        stroke: transparent;
        fill: ${(props) => props.textcolor};
        color: ${(props) => props.textcolor};
        animation: none;
        transition: fill 0.3s linear, width 0.05s, height 0.05s;
    }

    .aat-logo {
        line-height: 1;
        ${Device.tabletLandscape} {
            position: absolute;
            top: 50%;
            left: 1.5rem;
            transform: translateY(-50%);
        }
        ${Device.desktop} {
            left: 3rem;
        }
        &__default {
            display: none;
            width: 98px;
            height: 98px;
            ${Device.tabletLandscape} {
                display: block;
            }
            svg {
                width: 100%;
                height: 100%;
            }
            .app-bar--minimal & {
                display: none;
            }
        }
        &__type {
            width: 128px;
            height: 60px;
            overflow: hidden;
            display: block;
            position: relative;
            ${Device.tabletLandscape} {
                display: none;
            }
            svg {
                width: 100%;
                position: absolute;
                top: -30.5px;
                left: 0;
            }
            .app-bar--minimal & {
                display: block;
            }
        }
    }
`;

const AppBarTransition = styled.div`
    .app-bar--visible {
        visibility: visible;
        transform: translate(0, 0);
        transition: transform 200ms ease-in, visibility 200ms ease-in;
    }
    .app-bar--hidden {
        visibility: hidden;
        transform: translate(0, -100%);
        transition: transform 200ms ease-out, visibility 200ms ease-out;
    }
`;

interface AppBarProps {
    current: number;
    drawer: boolean;
    loginOnClick: () => void;
    basketOnClick: () => void;
    menuItems: Array<MenuItem>;
    textColor: string;
    overContent?: boolean;

    menuItemOnClick: (menuItem: MenuItem) => void;
    burgerButtonOnClick: () => void;
    logoOnClick: () => void;
}

const AppBar: React.FC<AppBarProps> = (props) => {
    const { textColor, menuItemOnClick, burgerButtonOnClick, logoOnClick } = props;
    const appBarWrapElement = createRef<HTMLDivElement>();
    const [isMinimal, setMinimal] = useState(false);
    const location = useLocation();
    const isOpen = props.current > 0;

    /**
     * Override minimal text color
     */
    const privateTextColor = isMinimal && !isOpen ? "#000" : textColor;

    useEffect(() => {
        let prevScrollPos = window.scrollY;
        const appBar = appBarWrapElement.current?.firstElementChild as HTMLDivElement;
        const windowHeight = window.innerHeight;

        if (appBar) {
            const scrollHandler = () => {
                const currentScrollPos = window.scrollY;
                const distance = prevScrollPos - currentScrollPos;
                const isDown = prevScrollPos < currentScrollPos;
                const isUp = prevScrollPos > currentScrollPos;
                const clientHeight = appBar.clientHeight;
                const offsetTop = parseInt(appBar.style.top) || 0;

                // Set minimal when scrolled past app bar height.
                setMinimal(currentScrollPos > clientHeight);

                // Set animate visible when scrolled past window height.
                const animateVisible = currentScrollPos > windowHeight;
                if (isOpen) {
                    // Sticky top
                    appBar.style.position = "fixed";
                    appBar.style.top = 0 + "px";
                } else if (isDown && offsetTop > 0 && !animateVisible) {
                    // Scrolling down
                    const max = -1 * clientHeight;
                    const value = Math.max(offsetTop + distance, max);
                    appBar.style.top = value + "px";
                } else if (isUp && !animateVisible) {
                    // Scrolling up
                    const value = Math.min(offsetTop + distance, 0);
                    appBar.style.top = value + "px";
                } else if (isDown && animateVisible) {
                    //Scrolling down & over window height, add animation
                    appBar.classList.remove("app-bar--visible");
                    appBar.classList.add("app-bar--hidden");
                } else if (isUp && animateVisible) {
                    //Scrolling up & over window height, add animation
                    appBar.classList.remove("app-bar--hidden");
                    appBar.classList.add("app-bar--visible");
                }
                prevScrollPos = currentScrollPos;
            };
            window.addEventListener("scroll", scrollHandler);

            return () => {
                window.removeEventListener("scroll", scrollHandler);
            };
        }
    }, [appBarWrapElement, isOpen]);

    const getAppBarWrapClassName = () => {
        const className: string[] = [];
        if (props.current !== -1) {
            className.push("app-bar--dropdown");
        }
        return className.join(" ");
    };

    const handleMenuItemClick = (menuItem: MenuItem) => () => menuItemOnClick(menuItem);

    return (
        <>
            <AppBarTransition>
                <AppBarWrap defaultColor={privateTextColor} className={getAppBarWrapClassName()} overContent={props.overContent} ref={appBarWrapElement}>
                    <AppBarInnerWrap className={isMinimal && !isOpen ? "app-bar--minimal" : ""}>
                        <AppBarContainer>
                            <BurgerButton active={props.drawer} onClick={burgerButtonOnClick} mobileColor={privateTextColor} />

                            <AppBarLogo as={Anchor} to="/" onClick={logoOnClick} textcolor={privateTextColor}>
                                <div className="aat-logo">
                                    <span className="aat-logo__default">
                                        <Icon name="aat-logo" />
                                    </span>
                                    <span className="aat-logo__type">
                                        <Icon name="aat-logotype" />
                                    </span>
                                </div>
                            </AppBarLogo>

                            <PrimaryNavigation>
                                {props.menuItems.map((menuItem) => {
                                    const btnClassName = ["menu-item-btn"];
                                    if (menuItem.id === props.current || (props.current < 0 && menuItem.url === location.pathname)) {
                                        btnClassName.push("button--active");
                                    }

                                    return (
                                        <button key={menuItem.id} role="button" className={btnClassName.join(" ")} onClick={handleMenuItemClick(menuItem)} style={{ color: privateTextColor }}>
                                            {menuItem.name}
                                        </button>
                                    );
                                })}
                            </PrimaryNavigation>
                            <UserStatus textColor={privateTextColor}/>
                        </AppBarContainer>
                    </AppBarInnerWrap>
                </AppBarWrap>
            </AppBarTransition>
        </>
    );
};

export default AppBar;
